import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { UserService } from '../core/user';
import { BehaviorSubject, firstValueFrom, forkJoin, Observable, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { CalendarAutomationModel, CalendarEventsModel } from '../models/calendar/calendar-events.model';
import moment, { Moment } from 'moment-timezone';
import { ICalendarEvent } from './cfa-calendar-module/interfaces/ICalendarEvent';
import { CalendarConstants } from './calendar.constants';
import { Promotion, setCachedPromotions } from '../models/cache/promotion.model';
import { CampaignType } from '../models/engage/campaign-details.model';
import { AnalyticsService } from '../core/analytics';
import { AudienceSource } from '../meet/dependencies/audience-source';
import { DigitalMarketingAudienceSource } from '../meet/dependencies/digital-marketing-audience-source';
import { AutomationSource } from 'spotlight-ui/app/discover/dependencies/automationsSource';
import {
  DigitalMarketingAutomationSource
} from 'spotlight-ui/app/discover/dependencies/digital-marketing-automation-source';

@Injectable()
export class CalendarService {
  private loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
  private allCampaigns: ICalendarEvent[] = []
  private searchObject: any;
  private campaignsSource: BehaviorSubject<ICalendarEvent[]> = new BehaviorSubject<ICalendarEvent[]>([])

  private allDayTypes: Set<string> = new Set([
    CalendarConstants.email,
    CampaignType.Notification,
    CalendarConstants.offer,
    CampaignType.Reward,
    CampaignType.Automation,
    CalendarConstants.nationalEmail,
    CalendarConstants.discover,
    CalendarConstants.thirdPartyPromotion,
    CalendarConstants.freeItemInCartPromotion,
    CalendarConstants.bonusPoint,
  ])

  constructor(
    private readonly us: UserService,
    private readonly http: HttpClient,
    private readonly analyticsService: AnalyticsService,
  ) { }

  private readonly audienceSource: AudienceSource = new DigitalMarketingAudienceSource(this.http);
  private readonly automationSource: AutomationSource = new DigitalMarketingAutomationSource(this.http);

  public getCampaigns(): Observable<ICalendarEvent[]> {
    return this.campaignsSource.asObservable()
  }

  public isLoading(): Observable<boolean> {
    return this.loading.asObservable()
  }

  public populateCalendarState( payload): void {
    this.loading.next(true)
    this.getCalendarEvents(payload).pipe(take(1)).subscribe((campaigns: any) => {
      const mappedEvents: ICalendarEvent[] = [];
      const seenIDs: Map<string, number> = new Map();
      campaigns.forEach((listOfCampaignsByType: any[]) => {
        listOfCampaignsByType.forEach((campaign: any) => {
          mappedEvents.push(this.calendarResponseToICalendarEvent(campaign))
          seenIDs.set(campaign.id, mappedEvents.length-1)
        })
      })
      this.applyPromotionsCache(seenIDs, mappedEvents)
      this.allCampaigns = mappedEvents
      if (this.searchObject) {
        this.filterCampaigns(this.searchObject)
        return
      }
      this.campaignsSource.next(this.allCampaigns)
      this.loading.next(false)
    });
  }

  public getCalendarEvents(payload): Observable<any> {
    if (!payload.campaignTypes) {
      return
    }
    return this.us.getSelectedLocation().pipe(
      switchMap((location) => {
        let locationNumber = location.locationNumber;
        let observables: Observable<ICalendarEvent[]>[] = [];
        payload.campaignTypes.forEach((type)=> {
          observables = observables.concat((this.getCalendarEventsByType(payload, type)));
          if (payload.nationalEmails && type === 'holidaysAndCampaigns') {
            observables = observables.concat((this.getNationalEmailsAndMapToCalendarEvents(payload)));
          }
        });
        if (payload.campaignEngineV2 && payload.campaignTypes.includes('rewards') ) {
          const automationsAndRewards = this.getCampaignsAndMapToCalendarEvents(payload, CampaignType.Reward, locationNumber).pipe(
            switchMap((rewards) => {
              if (payload.anyAutomations) {
                return this.getAutomationsAndMapToCalendarEvents(rewards, payload, locationNumber);
              }
              rewards = this.filterAutomatedRewards(rewards);
              return of(rewards);
            })
          );
          observables = observables.concat(automationsAndRewards);
        }
        if (payload.campaignEngineV2Email && payload.campaignTypes.includes('emails')) {
          observables = observables.concat((this.getCampaignsAndMapToCalendarEvents(payload, CampaignType.Notification, locationNumber)));
        }
        if (payload.campaignEngineV2Event && payload.campaignTypes.includes('events')) {
          observables = observables.concat((this.getCampaignsAndMapToCalendarEvents(payload, CampaignType.Event, locationNumber)));
        }
        return forkJoin(observables)
      })
    )

  }

  private filterAutomatedRewards(rewards: any[]) {
    return rewards.filter(reward => reward.createdBy.toUpperCase() != CampaignType.Automation);
  }

  private calendarResponseToICalendarEvent(campaign: any): ICalendarEvent {
    if (campaign.type === CalendarConstants.discover) {
      campaign.type = CalendarConstants.offer
    }
    let startDate: Moment = moment(campaign.startDate);
    let endDate: Moment = moment(campaign.endDate);
    let displayAsAllDay: boolean = this.allDayTypes.has(campaign.type);

    const isSameDay = startDate.isSame(endDate, 'day');

    if ([CalendarConstants.bonusPoint, CalendarConstants.freeItemInCartPromotion].includes(campaign.type) &&
                isSameDay && campaign.startTime && campaign.endTime) {
      displayAsAllDay = false;
      startDate = moment(`${startDate.format('DD/MM/YYYY')} ${campaign.startTime}`, 'DD/MM/YYYY h:mm a');
      endDate = moment(`${endDate.format('DD/MM/YYYY')} ${campaign.endTime}`, 'DD/MM/YYYY h:mm a');
    }

    if ([CalendarConstants.thirdPartyPromotion, CalendarConstants.freeItemInCartPromotion].includes(campaign.type) &&
                campaign.stopDate) {
      endDate = moment(campaign.stopDate);
    }

    let categoryId: string
    if (campaign.automatedCampaign) {
      categoryId = 'automation'
    } else if (campaign.cfaIds && (campaign.type == CalendarConstants.offer || campaign.type == CampaignType.Reward)
        && campaign.cfaIds.length == 1) {
      categoryId = 'singleRecipient'
    }
    return {
      ...campaign,
      calendarId: campaign.type,
      categoryId: categoryId,
      startDate,
      endDate,
      displayAsAllDay,
    }
  }

  private applyPromotionsCache(seenIDs: Map<string, number>, mappedEvents: ICalendarEvent[]): void {
    let locationNumber: string
    firstValueFrom(this.us.getSelectedLocation()).then((location: string) => locationNumber = location)
    this.allDayTypes.forEach((type: string) => {
      let cacheName = 'pendingPromotions:' + locationNumber + ':' + type
      let pendingPromotions: Promotion[] = JSON.parse(localStorage.getItem(cacheName))
      pendingPromotions = pendingPromotions ? pendingPromotions : []
      const stillPending: Promotion[] = []
      pendingPromotions.forEach(promotion => {
        if (seenIDs.has(promotion.id) && promotion.status == 'pendingDeletion') {
          mappedEvents.splice(seenIDs.get(promotion.id), 1)
          stillPending.push(promotion)
        } else if (!seenIDs.has(promotion.id) && promotion.status == 'pendingCreation') {
          mappedEvents.push({
            ...promotion,
            startDate: moment(promotion.startDate),
            endDate: moment(promotion.endDate)
          })
          stillPending.push(promotion)
        }
      })
      setCachedPromotions(stillPending, cacheName)
    })
  }

  private getCalendarEventsByType(payload, type) : Observable<any> {
    return this.us.getSelectedLocation().pipe(
      switchMap((location) => {
        const { locationNumber } = location;
        const url = `/api/location/${locationNumber}/calendarEvents?campaignType=${type}`
        return this.http.post(environment.spotlightApiUrl + url, payload);
      }),
      map((res: any) => { return res.responseObject }),
      catchError(() => {
        //returns an empty array for that type with out failing that entire flow
        return of([]);
      })
    );
  }

  private getCampaignsAndMapToCalendarEvents(payload, type, locationNumber): Observable<any[]> {
    return this.fetchCampaigns(locationNumber, payload, type).pipe(
      switchMap((campaigns) => {
        if (!campaigns) {
          return of([]);
        }
        return this.enrichCampaignsWithAudience(campaigns, locationNumber);
      }),
      map((enrichedCampaigns) => this.mapCalendarEvents(enrichedCampaigns))
    )
  }

  private fetchCampaigns(location, payload, type): Observable<any[]> {
    const headers = new HttpHeaders({
      'Requested-Localities': location
    });
    const campaignsUrl = `${environment.digitalMarketingCampaignsServiceURL}/api/restaurant/campaigns`
        + `?startDate=${payload.fromDate}T00:00:00Z&endDate=${payload.toDate}T23:59:00Z&type=${type}`;
    return this.http.get(campaignsUrl, { headers }).pipe(
      map((campaigns: any[]) => campaigns),
      catchError(() => {
        return of( []);
      })
    );
  }

  private enrichCampaignsWithAudience(campaigns: any[], locationNumber: string): Observable<any[]> {
    const audienceIds = campaigns?.map(camp => camp.audienceID).filter(id => id != null) || [];
    if (audienceIds.length === 0) {
      return of(campaigns);
    }
    return this.audienceSource.populateCalculatedAudiences(locationNumber, audienceIds).pipe(
      map((calculatedAudiences) => {
        return campaigns.map(campaign => {
          const originalRecipients = campaign.recipients?.length > 0 ? campaign.recipients : [];
          const audienceRecipients = calculatedAudiences.find(aud => aud.audienceID === campaign.audienceID)?.customerIDs || [];
          return {
            ...campaign,
            recipients: audienceRecipients.length > 0 ? audienceRecipients : originalRecipients
          };
        });
      }),
      catchError(()=>of(campaigns))
    );
  }

  private getAutomationsAndMapToCalendarEvents(rewards, payload, locationNumber: string): Observable<any[]> {
    return this.automationSource.fetchAutomations( payload, locationNumber).pipe(
      switchMap((automations ) => {
        if (!automations) {
          automations = []
        }
        let automationsWithRewardInfo = this.mapCalendarAutomationEvents(this.enrichAutomationsWithRewardInfo(rewards, automations))
        rewards = this.filterAutomatedRewards(rewards);
        return of([...rewards, ...automationsWithRewardInfo])
      })
    );
  }

  private enrichAutomationsWithRewardInfo(rewards: any[], automations: any[]) {
    return automations.map((automation) => {
      let customers = [];
      let rewardCampaigns = []
      for (let id of automation.campaignIDs ?? []) {
        const reward = rewards.find((reward) => reward.id === id);
        customers = customers.concat(reward?.cfaIds ?? []);
        rewardCampaigns.push(reward)
      }
      return { ...automation, cfaIds: customers, rewardCampaigns };
    });

  }

  private getNationalEmailsAndMapToCalendarEvents(payload) : Observable<any[]> {
    return this.us.getSelectedLocation().pipe(
      switchMap((location) => {
        const headers = new HttpHeaders({
          'Requested-Localities': location.locationNumber
        });
        return this.http.get(
          environment.digitalMarketingCampaignsServiceURL +
          `/api/national/campaigns?startDate=${payload.fromDate}T00:00:00Z&endDate=${payload.toDate}T23:59:00Z`,
          { headers: headers })
      }),
      map((res: any) => {
        res = res.map(nationalEmail => ({
          ...nationalEmail,
          type: CalendarConstants.nationalEmail
        }));
        return this.mapCalendarEvents(res);
      }),
      catchError(() => {
        return of([]);
      })
    );
  }

  private mapCalendarEvents(data: CalendarEventsModel[]) {
    return data.map(d => {
      return {
        id: d.id,
        type: d.type,
        title: d.name,
        templateTitle: d.title,
        campaignName: d.name,
        subject: d.subject,
        startDate: moment(d.startDatetime).valueOf(),
        endDate: moment(d.endDatetime).valueOf(),
        cfaIds: d.recipients,
        automatedCampaign: false,
        campaignEngineV2: true,
        createdBy: d.createdBy ?? ''
      }
    })
  }

  private mapCalendarAutomationEvents(data: CalendarAutomationModel[]) {
    return data.map(d => {
      return {
        id: d.id,
        type: CampaignType.Automation,
        title: d.name,
        templateTitle: d.campaignTemplateID,
        campaignName: d.name,
        subject: d.customizations.subject,
        startDate: moment(d.createdAt).valueOf(),
        endDate: d.deletedAt ? moment(d.deletedAt).valueOf() : moment().add(1, 'year').valueOf(),
        cfaIds: d.cfaIds,
        automatedCampaign: true,
        campaignEngineV2: true,
        campaignIDs: d.campaignIDs,
        rewardCampaigns: d.rewardCampaigns
      }
    })
  }

  public filterCampaigns(searchObject): void {
    this.analyticsService.sendEvent(
      'Filter Campaigns',
      { category: searchObject.category, searchBar: 'Calendar' }
    );
    this.loading.next(true);
    this.searchObject = searchObject;
    const filteredCampaigns: ICalendarEvent[] = []
    switch (searchObject.category) {
      case 'Customer Name':
        filteredCampaigns.push(...this.allCampaigns.filter(
          (campaign: ICalendarEvent) => campaign.cfaIds && campaign.cfaIds.includes(searchObject.cfaid)
        ))
        break
      case 'Template Title':
        filteredCampaigns.push(...this.allCampaigns.filter(
          (campaign: ICalendarEvent) => new RegExp(`.*${searchObject.searchTerm}.*`, 'i').test(campaign.templateTitle)
        ))
        break
      case 'Internal Campaign Name':
        filteredCampaigns.push(...this.allCampaigns.filter(
          (campaign: ICalendarEvent) => new RegExp(`.*${searchObject.searchTerm}.*`, 'i').test(campaign.title)
        ))
        break
      case 'Subject Line':
        filteredCampaigns.push(...this.allCampaigns.filter(
          (campaign: ICalendarEvent) => new RegExp(`.*${searchObject.searchTerm}.*`).test(campaign.subject)
        ))
        break
    }
    this.campaignsSource.next(filteredCampaigns)
    this.loading.next(false)
  }

  public resetCampaignsList(): void {
    this.loading.next(true)
    this.campaignsSource.next(this.allCampaigns)
    this.loading.next(false)
  }
}
